import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogDismiss,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Skeleton,
} from '@barfacil/barfacil-ui'
import { ArrowRightLeft, Search, Store } from 'lucide-react'
import {
  PropsWithChildren,
  startTransition,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useUpdateCurrentCompany } from '@/app/panel/_hooks/use-update-current-company'
import { NonExistentUserFeedbackAlertDialog } from '@/components/ui/non-existent-user-dialog'
import { SpinnerDialog } from '@/components/ui/spinner-dialog'
import {
  Virtualizer,
  VirtualizerError,
  VirtualizerList,
  VirtualizerLoading,
  VirtualizerScrollToOffset,
} from '@/components/ui/virtualizer'
import { screenSize } from '@/config/screens'
import { useCompanyContext } from '@/contexts/company-context'
import { useWindowSize } from '@/hooks/use-window-size'
import { cn, removePropsFromObject } from '@/lib/utils'

type CompanyPickerProps = {
  onClose: () => void
}

type CompanyPickerMessageProps = PropsWithChildren & {
  className?: string
}

function CompanyPickerLoading() {
  return (
    <div>
      {Array.from({ length: 5 }).map((_, index) => (
        <div
          className="flex items-center gap-2 border-b border-gray-100 px-6 py-3.5"
          key={index}
        >
          <Skeleton className="mr-2 size-4 shrink-0 rounded-full" />

          <Skeleton className="size-6 shrink-0 rounded-full" />

          <Skeleton className="h-4 w-full md:w-[12.5rem]" />

          <Skeleton className="ml-auto hidden h-4 shrink-0 md:block md:w-40" />
        </div>
      ))}
    </div>
  )
}

const CompanyPickerMessage = ({
  className,
  children,
}: CompanyPickerMessageProps) => {
  return (
    <div
      className={cn(
        'px-6 py-4 text-center text-sm font-semibold text-gray-500',
        className,
      )}
    >
      {children}
    </div>
  )
}

const CompanyPickerResumed = ({ onClose }: CompanyPickerProps) => {
  const { currentCompany, companies } = useCompanyContext()

  const { mutation, openDialog, closeDialog } = useUpdateCurrentCompany()

  const [openSpinnerDialog, setOpenSpinnerDialog] = useState(false)

  function handleOnSelectCompany(id: number) {
    setOpenSpinnerDialog(true)

    mutation.mutate(
      { id },
      {
        onSuccess: () => onClose(),
        onSettled: () => setOpenSpinnerDialog(false),
      },
    )
  }

  return (
    <div>
      <div className="my-2 h-10 px-4 py-2">
        <span className="text-sm leading-6">Trocar empresa</span>
      </div>

      <div className="flex flex-col gap-2 pb-4">
        {companies.map((company) => (
          <button
            key={company.cod_empresa}
            className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-none disabled:pointer-events-none"
            role="option"
            type="button"
            disabled={company.cod_empresa === currentCompany?.cod_empresa}
            aria-selected={company.cod_empresa === currentCompany?.cod_empresa}
            onClick={() => handleOnSelectCompany(company.cod_empresa)}
          >
            <Avatar className="size-6 translate-y-0.5">
              <AvatarImage
                src=""
                width={24}
                height={24}
                alt={`Logomarca ${company?.razao_social}`}
                loading="lazy"
              />

              <AvatarFallback className="text-[10px]">
                <Store size={12} />
              </AvatarFallback>
            </Avatar>

            <span className="text-left font-medium text-gray-500 md:line-clamp-1">
              {company?.razao_social}
            </span>
          </button>
        ))}
      </div>

      <NonExistentUserFeedbackAlertDialog
        open={openDialog}
        onClose={closeDialog}
      />

      <SpinnerDialog
        open={openSpinnerDialog}
        onOpenChange={setOpenSpinnerDialog}
      />
    </div>
  )
}

const CompanyPicker = ({ onClose }: CompanyPickerProps) => {
  const {
    currentCompany,
    companiesInfinite,
    companies,
    totalCompanies,
    setCompaniesFilters: setFilters,
  } = useCompanyContext()

  const { mutation, openDialog, closeDialog } = useUpdateCurrentCompany()

  const [open, setOpen] = useState(false)
  const [openSpinnerDialog, setOpenSpinnerDialog] = useState(false)

  const [search, setSearch] = useState('')
  const [resetScroll, setResetScroll] = useState(false)

  const { width } = useWindowSize()
  const maxHeight = useMemo(() => (width > screenSize.LG ? 424 : 312), [width])

  function handleOpen(dialogOpen: boolean) {
    if (dialogOpen) {
      setFilters((prevFilters) =>
        removePropsFromObject(prevFilters, ['search', 'page']),
      )
      setSearch('')
    }

    setOpen(dialogOpen)
  }

  function handleSearch(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.currentTarget.value

    setResetScroll(true)

    setSearch(value)

    startTransition(() => {
      setFilters((prevFilters) => {
        if (value) {
          return {
            ...prevFilters,
            search: value,
          }
        }

        return removePropsFromObject(prevFilters, ['search'])
      })
    })
  }

  function handleNextPage() {
    if (
      companiesInfinite.hasNextPage &&
      !companiesInfinite.isFetchingNextPage &&
      !companiesInfinite.isError
    ) {
      return companiesInfinite.fetchNextPage()
    }

    return new Promise<void>((resolve) => resolve())
  }

  function handleOnSelectCompany(id: number) {
    setOpenSpinnerDialog(true)

    mutation.mutate(
      { id },
      {
        onSuccess: () => {
          setOpen(false)
          onClose()
        },
        onSettled: () => setOpenSpinnerDialog(false),
      },
    )
  }

  useEffect(() => {
    return () => setResetScroll(false)
  })

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpen}>
        <DialogTrigger asChild>
          <button className="my-2 flex h-10 w-full flex-1 cursor-pointer items-center gap-2 px-4 py-2 text-sm outline-none transition-colors hover:bg-gray-100 hover:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50">
            <ArrowRightLeft className="text-gray-500" size={24} />
            Trocar empresa
          </button>
        </DialogTrigger>

        <DialogContent className="w-[47.5rem] gap-8">
          <DialogHeader>
            <DialogTitle className="leading-normal">
              Alternar empresa
            </DialogTitle>
          </DialogHeader>

          <DialogDescription className="hidden" />

          <DialogBody>
            {totalCompanies > 12 && (
              <div className="mb-4 flex items-center gap-x-2 gap-y-1 rounded border border-gray-100 bg-gray-50 shadow-sm sm:h-10">
                <div className="shrink-0 pl-2 text-gray-300">
                  <Search size={24} />
                </div>

                <div className="flex-1">
                  <Input
                    className="h-8 border-0 bg-transparent px-2 text-sm placeholder:text-gray-300 disabled:bg-transparent"
                    id="input-search-company"
                    type="search"
                    value={search}
                    autoComplete="off"
                    placeholder="Buscar uma empresa"
                    onChange={handleSearch}
                  />
                </div>
              </div>
            )}

            <div className="rounded border border-gray-100" role="group">
              {companiesInfinite.isLoading ? (
                <CompanyPickerLoading />
              ) : companies.length === 0 && companiesInfinite.isError ? (
                <CompanyPickerMessage className="text-red-500">
                  Ocorreu um erro ao carregar as empresas.
                </CompanyPickerMessage>
              ) : companies.length === 0 ? (
                <CompanyPickerMessage>
                  Nenhuma empresa encontrada.
                </CompanyPickerMessage>
              ) : (
                <Virtualizer options={{ count: companies.length }}>
                  {resetScroll && <VirtualizerScrollToOffset offset={0} />}

                  <VirtualizerList
                    options={{ maxHeight, onScrollEnd: handleNextPage }}
                  >
                    {(virtualRow) => {
                      const company = companies[virtualRow.index]
                      const {
                        cod_empresa: id,
                        razao_social: nome,
                        documento: cnpj,
                      } = company
                      const isSelected = id === currentCompany?.cod_empresa

                      return (
                        <button
                          className="flex w-full items-center gap-2 border-b border-gray-100 px-6 py-3.5 text-sm hover:bg-gray-50 focus-visible:bg-gray-100 focus-visible:outline-none disabled:pointer-events-none"
                          role="option"
                          type="button"
                          disabled={isSelected || mutation.isPending}
                          aria-selected={isSelected}
                          onClick={() =>
                            handleOnSelectCompany(company.cod_empresa)
                          }
                        >
                          <Avatar className="size-6 translate-y-0.5">
                            <AvatarImage
                              src=""
                              width={24}
                              height={24}
                              alt={`Logomarca ${nome}`}
                              loading="lazy"
                            />

                            <AvatarFallback className="text-[10px]">
                              <Store size={12} />
                            </AvatarFallback>
                          </Avatar>

                          <span className="text-left font-medium text-gray-500 md:line-clamp-1">
                            {nome}
                          </span>

                          <span className="ml-auto hidden shrink-0 text-gray-300 md:block">
                            {cnpj}
                          </span>
                        </button>
                      )
                    }}
                  </VirtualizerList>

                  {companiesInfinite.isFetchingNextPage && (
                    <VirtualizerLoading>
                      <CompanyPickerLoading />
                    </VirtualizerLoading>
                  )}

                  {companies.length > 0 && companiesInfinite.isError && (
                    <VirtualizerError>
                      <CompanyPickerMessage className="text-red-500">
                        Ocorreu um erro ao carregar mais empresas.
                      </CompanyPickerMessage>
                    </VirtualizerError>
                  )}
                </Virtualizer>
              )}
            </div>
          </DialogBody>

          <DialogDismiss className="absolute right-6 top-6" />
        </DialogContent>
      </Dialog>

      <NonExistentUserFeedbackAlertDialog
        open={openDialog}
        onClose={closeDialog}
      />

      <SpinnerDialog
        open={openSpinnerDialog}
        onOpenChange={setOpenSpinnerDialog}
      />
    </>
  )
}

export { CompanyPicker, CompanyPickerResumed }
